import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, CheckCircle2, XCircle } from "lucide-react";
import { PRIORITY_META, STATUS_LABEL } from "./TasksPage";

const DEPARTMENTS = ["kitchen", "dessert", "admin", "event", "driver", "store"];
const TABS = ["Overview", "Tasks", "Review", "Templates", "Team", "Reports"];

export default function TaskManagerPage() {
  const [tab, setTab] = useState("Overview");
  const [dash, setDash] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [report, setReport] = useState(null);
  const [period, setPeriod] = useState("weekly");
  const [editing, setEditing] = useState(null);
  const [filterDept, setFilterDept] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const load = () => {
    api.get("/tasks/dashboard/manager").then((r) => setDash(r.data)).catch(() => {});
    api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
    api.get("/task-templates").then((r) => setTemplates(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "Reports") api.get(`/tasks/reports?period=${period}`).then((r) => setReport(r.data)).catch(() => {});
  }, [tab, period]);

  const review = async (t, approve) => {
    const note = approve ? "" : (window.prompt("Rejection reason (optional):") || "");
    try {
      await api.post(`/tasks/${t.id}/review`, { approve, note });
      toast.success(approve ? "Task approved" : "Task rejected & returned");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removeTask = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try { await api.delete(`/tasks/${t.id}`); toast.success("Task deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const visibleTasks = tasks.filter((t) => (!filterDept || t.department === filterDept) && (!filterStatus || t.status === filterStatus));

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="task-manager-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Operations</div>
          <h1 className="font-display text-4xl mt-2">Task Manager</h1>
        </div>
        <Button onClick={() => setEditing({})} data-testid="new-task-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">
          <Plus className="w-4 h-4 mr-1.5" /> New Task
        </Button>
      </div>

      <div className="flex gap-1 mb-6 flex-wrap">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`tm-tab-${t.toLowerCase()}`}
            className={`px-3 py-1.5 text-xs rounded-sm border ${tab === t ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}>
            {t}{t === "Review" && dash?.totals?.waiting_review > 0 ? ` (${dash.totals.waiting_review})` : ""}
          </button>
        ))}
      </div>

      {tab === "Overview" && dash && (
        <div className="space-y-6" data-testid="tm-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["Total Tasks", dash.totals.tasks, "#D4AF37"], ["Completed", dash.totals.completed, "#10B981"],
              ["Overdue", dash.totals.overdue, "#EF4444"], ["Pending Review", dash.totals.waiting_review, "#F59E0B"]].map(([l, v, c]) => (
              <div key={l} className="card-lux p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
                <div className="text-2xl font-display mt-1" style={{ color: c }}>{v}</div>
              </div>
            ))}
          </div>
          <div className="card-lux p-5">
            <h3 className="font-display text-lg mb-3">Department Performance</h3>
            <div className="space-y-2.5">
              {dash.departments.map((d) => (
                <div key={d.department}>
                  <div className="flex justify-between text-xs mb-1"><span className="capitalize text-zinc-300">{d.department}</span><span className="text-zinc-500">{d.completed}/{d.total} · {d.completion_rate}%{d.overdue ? ` · ${d.overdue} overdue` : ""}</span></div>
                  <div className="h-1.5 rounded-full bg-[#1a1a1a]"><div className="h-full rounded-full bg-[#D4AF37]" style={{ width: `${d.completion_rate}%` }} /></div>
                </div>
              ))}
              {dash.departments.length === 0 && <div className="text-xs text-zinc-600">No tasks yet</div>}
            </div>
          </div>
          <div className="card-lux p-5 overflow-x-auto">
            <h3 className="font-display text-lg mb-3">Employee Productivity</h3>
            <table className="w-full text-xs">
              <thead><tr className="text-zinc-500 text-left"><th className="py-2">Employee</th><th>Dept</th><th>Tasks</th><th>Done</th><th>Overdue</th><th>Review</th><th>Rate</th><th>Avg Time</th><th>Productivity</th></tr></thead>
              <tbody>
                {dash.employees.map((e) => (
                  <tr key={e.id} className="border-t border-[#1a1a1a]" data-testid={`emp-row-${e.id}`}>
                    <td className="py-2 text-zinc-200">{e.name}</td>
                    <td className="capitalize text-zinc-500">{e.department || "—"}</td>
                    <td>{e.total}</td><td className="text-emerald-400">{e.completed}</td>
                    <td className={e.overdue ? "text-red-400" : ""}>{e.overdue}</td><td>{e.pending_review}</td>
                    <td>{e.completion_rate}%</td><td>{e.avg_minutes ? `${e.avg_minutes}m` : "—"}</td>
                    <td>{e.productivity == null ? <span className="text-zinc-600">—</span> : <span className={`font-medium ${e.productivity >= 70 ? "text-emerald-400" : e.productivity >= 40 ? "text-amber-400" : "text-red-400"}`}>{e.productivity}</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "Tasks" && (
        <div data-testid="tm-tasks">
          <div className="flex gap-2 mb-4 flex-wrap">
            <select value={filterDept} onChange={(e) => setFilterDept(e.target.value)} className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-9 text-sm">
              <option value="">All departments</option>
              {DEPARTMENTS.map((d) => <option key={d} value={d} className="capitalize">{d}</option>)}
            </select>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-9 text-sm">
              <option value="">All statuses</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            {visibleTasks.slice(0, 100).map((t) => {
              const pm = PRIORITY_META[t.priority] || PRIORITY_META.medium;
              return (
                <div key={t.id} className="flex items-center justify-between gap-3 p-3 rounded-sm bg-[#0a0a0a] border border-[#1a1a1a]" style={{ borderLeft: `3px solid ${pm.color}` }} data-testid={`tm-task-${t.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{t.title}</div>
                    <div className="text-[11px] text-zinc-500">{t.assignee_name} · <span className="capitalize">{t.department}</span> · {STATUS_LABEL[t.status]}{t.due_at ? ` · due ${t.due_at.slice(0, 16).replace("T", " ")}` : ""}{t.source !== "manual" ? ` · ${t.source}` : ""}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button onClick={() => setEditing(t)} variant="outline" size="sm" className="border-[#2a2a2a] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-sm h-7 w-7 p-0" data-testid={`edit-task-${t.id}`}><Pencil className="w-3 h-3" /></Button>
                    <Button onClick={() => removeTask(t)} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-sm h-7 w-7 p-0" data-testid={`delete-task-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              );
            })}
            {visibleTasks.length === 0 && <div className="text-sm text-zinc-600 py-8 text-center">No tasks match</div>}
          </div>
        </div>
      )}

      {tab === "Review" && dash && (
        <div className="space-y-3" data-testid="tm-review">
          {dash.review_queue.map((t) => (
            <div key={t.id} className="p-4 rounded-sm bg-[#0a0a0a] border border-amber-500/30" data-testid={`review-task-${t.id}`}>
              <div className="text-sm text-zinc-100">{t.title}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">{t.assignee_name} · <span className="capitalize">{t.department}</span></div>
              {(t.photos || []).length > 0 && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {t.photos.map((p) => <img key={p.id} src={p.data_url} alt="" className="w-16 h-16 object-cover rounded-sm border border-[#2a2a2a]" />)}
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <Button onClick={() => review(t, true)} data-testid={`approve-task-${t.id}`} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500 text-white"><CheckCircle2 className="w-3 h-3 mr-1" /> Approve</Button>
                <Button onClick={() => review(t, false)} data-testid={`reject-task-${t.id}`} variant="outline" className="h-8 text-xs border-red-500/40 text-red-400 hover:bg-red-500/10"><XCircle className="w-3 h-3 mr-1" /> Reject</Button>
              </div>
            </div>
          ))}
          {dash.review_queue.length === 0 && <div className="text-sm text-zinc-600 py-8 text-center">Nothing waiting for review 🎉</div>}
        </div>
      )}

      {tab === "Templates" && <TemplatesTab templates={templates} onChanged={load} />}
      {tab === "Team" && dash && <TeamTab employees={dash.employees} onChanged={load} />}

      {tab === "Reports" && (
        <div data-testid="tm-reports">
          <div className="flex gap-2 mb-4">
            {["weekly", "monthly"].map((p) => (
              <button key={p} onClick={() => setPeriod(p)} data-testid={`report-period-${p}`}
                className={`px-3 py-1.5 text-xs rounded-sm border capitalize ${period === p ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400"}`}>{p}</button>
            ))}
          </div>
          <div className="card-lux p-5 overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-zinc-500 text-left"><th className="py-2">Employee</th><th>Tasks</th><th>Completed</th><th>Overdue</th><th>Completion %</th><th>Avg Time</th><th>Productivity</th></tr></thead>
              <tbody>
                {(report?.rows || []).map((r) => (
                  <tr key={r.assignee_id} className="border-t border-[#1a1a1a]">
                    <td className="py-2 text-zinc-200">{r.name}</td><td>{r.total}</td>
                    <td className="text-emerald-400">{r.completed}</td><td className={r.overdue ? "text-red-400" : ""}>{r.overdue}</td>
                    <td>{r.completion_rate}%</td><td>{r.avg_minutes ? `${r.avg_minutes}m` : "—"}</td>
                    <td className="font-medium text-[#D4AF37]">{r.productivity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!report || report.rows.length === 0) && <div className="text-sm text-zinc-600 py-6 text-center">No data for this period</div>}
          </div>
        </div>
      )}

      {editing && <TaskDialog task={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TaskDialog({ task, onClose, onSaved }) {
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({
    title: task.title || "", description: task.description || "",
    assignee_id: task.assignee_id || "", department: task.department || "kitchen",
    priority: task.priority || "medium", due_at: task.due_at ? task.due_at.slice(0, 16) : "",
    estimated_minutes: task.estimated_minutes || 30, requires_photo: task.requires_photo || false,
    notes: task.notes || "", repeat: false, repeat_days: [],
  });
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const toggleDay = (i) => setF((p) => ({ ...p, repeat_days: p.repeat_days.includes(i) ? p.repeat_days.filter((d) => d !== i) : [...p.repeat_days, i] }));
  useEffect(() => {
    api.get("/users/staff").then((r) => {
      setStaff(r.data);
      if (!task.assignee_id && r.data[0]) setF((p) => ({ ...p, assignee_id: r.data[0].id }));
    });
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    const body = { ...f, due_at: f.due_at ? `${f.due_at}:00` : null, estimated_minutes: Number(f.estimated_minutes) || 30 };
    try {
      if (task.id) { await api.patch(`/tasks/${task.id}`, body); toast.success("Task updated"); }
      else if (f.repeat && f.repeat_days.length > 0) { await api.post("/tasks", body); toast.success("Recurring task created — auto-repeats on selected days"); }
      else { await api.post("/tasks", body); toast.success("Task created & employee notified"); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const cls = "bg-[#050505] border-[#2a2a2a]";
  const sel = "w-full bg-[#050505] border border-[#2a2a2a] rounded-sm px-3 h-10 text-sm";
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{task.id ? "Edit Task" : "New Task"}</DialogTitle>
          <DialogDescription className="sr-only">Create or edit an employee task.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Task title" data-testid="task-title-input" className={cls} />
          <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" data-testid="task-desc-input" className={cls} />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.assignee_id} onChange={(e) => setF({ ...f, assignee_id: e.target.value })} data-testid="task-assignee-select" className={sel}>
              {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} data-testid="task-dept-select" className={sel}>
              {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="task-priority-select" className={sel}>
              <option value="high">🔴 High</option><option value="medium">🟡 Medium</option><option value="low">🟢 Low</option>
            </select>
            <div className="relative">
              <Input type="number" min="5" value={f.estimated_minutes} onChange={(e) => setF({ ...f, estimated_minutes: e.target.value })} title="Estimated duration (minutes)" className={`${cls} pr-12`} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider text-zinc-500 pointer-events-none">min</span>
              <span className="absolute -top-2 left-2 px-1 bg-[#0a0a0a] text-[9px] uppercase tracking-wider text-zinc-500">Est. duration</span>
            </div>
          </div>
          <Input type="datetime-local" value={f.due_at} onChange={(e) => setF({ ...f, due_at: e.target.value })} data-testid="task-due-input" className={cls} />
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={f.requires_photo} onCheckedChange={(v) => setF({ ...f, requires_photo: v })} data-testid="task-photo-switch" className="data-[state=checked]:bg-[#D4AF37]" />
            Require photo proof before completion
          </label>
          {!task.id && (
            <div className="p-3 rounded-sm bg-[#050505] border border-[#1a1a1a] space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={f.repeat} onCheckedChange={(v) => setF({ ...f, repeat: v })} data-testid="task-repeat-switch" className="data-[state=checked]:bg-emerald-500" />
                Auto repeat on selected working days
              </label>
              {f.repeat && (
                <>
                  <div className="flex gap-1.5 flex-wrap">
                    {DAYS.map((d, i) => (
                      <button key={d} type="button" onClick={() => toggleDay(i)} data-testid={`repeat-day-${d.toLowerCase()}`}
                        className={`px-2.5 py-1.5 text-xs rounded-sm border transition-colors ${f.repeat_days.includes(i) ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#2a2a2a] text-zinc-400 hover:border-[#D4AF37]"}`}>
                        {d}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-zinc-600">The task is auto-created for the employee every selected day{f.due_at ? ` (due ${f.due_at.slice(11, 16)} daily)` : " (due at shift end)"}. Manage rules under Templates → Recurring Tasks.</p>
                </>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button onClick={onClose} variant="outline" className="border-[#2a2a2a] rounded-sm">Cancel</Button>
            <Button onClick={save} disabled={!f.title || !f.assignee_id || (f.repeat && f.repeat_days.length === 0)} data-testid="save-task-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C86B] rounded-sm">{task.id ? "Save Changes" : f.repeat ? "Create Recurring Task" : "Create Task"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesTab({ templates, onChanged }) {
  const [adding, setAdding] = useState(null); // {department, kind}
  const [title, setTitle] = useState("");
  const [recs, setRecs] = useState([]);
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const loadRecs = () => api.get("/recurring-tasks").then((r) => setRecs(r.data)).catch(() => {});
  useEffect(() => { loadRecs(); }, []);

  const toggleRec = async (r) => { await api.patch(`/recurring-tasks/${r.id}`, { active: !r.active }); loadRecs(); };
  const removeRec = async (r) => {
    if (!window.confirm(`Delete recurring task "${r.title}" for ${r.assignee_name}?`)) return;
    await api.delete(`/recurring-tasks/${r.id}`); toast.success("Recurring task deleted"); loadRecs();
  };

  const addTpl = async () => {
    try {
      await api.post("/task-templates", { ...adding, title, priority: "medium" });
      toast.success("Template added");
      setAdding(null); setTitle("");
      onChanged();
    } catch (e) { toast.error("Failed"); }
  };
  const toggleTpl = async (t) => { await api.patch(`/task-templates/${t.id}`, { active: !t.active }); onChanged(); };
  const removeTpl = async (t) => {
    if (!window.confirm(`Delete template "${t.title}"?`)) return;
    await api.delete(`/task-templates/${t.id}`); toast.success("Deleted"); onChanged();
  };

  return (
    <div className="space-y-4" data-testid="tm-templates">
      <div className="card-lux p-4" data-testid="recurring-tasks-card">
        <h3 className="font-display text-lg mb-1">Recurring Tasks</h3>
        <p className="text-[11px] text-zinc-500 mb-2">Created via New Task → "Auto repeat on selected working days". Auto-generated each matching day.</p>
        <div className="space-y-1.5">
          {recs.map((r) => (
            <div key={r.id} className="flex items-center gap-2 text-xs px-2.5 py-2 rounded-sm bg-[#050505] border border-[#1a1a1a]" data-testid={`rec-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <span className={r.active ? "text-zinc-200" : "text-zinc-600 line-through"}>{r.title}</span>
                <span className="text-zinc-500"> · {r.assignee_name} · <span className="capitalize">{r.department}</span>{r.due_time ? ` · ${r.due_time}` : ""}</span>
              </div>
              <div className="flex gap-0.5">
                {DAYS.map((d, i) => (
                  <span key={d} className={`px-1 py-0.5 rounded-sm text-[9px] ${(r.repeat_days || []).includes(i) ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "text-zinc-700"}`}>{d[0]}</span>
                ))}
              </div>
              <Switch checked={!!r.active} onCheckedChange={() => toggleRec(r)} className="data-[state=checked]:bg-emerald-500 scale-75" data-testid={`rec-toggle-${r.id}`} />
              <button onClick={() => removeRec(r)} className="text-zinc-600 hover:text-red-400" data-testid={`rec-delete-${r.id}`}><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {recs.length === 0 && <div className="text-xs text-zinc-600 py-2">No recurring tasks yet — create one via New Task with the repeat toggle.</div>}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
      {DEPARTMENTS.map((d) => (
        <div key={d} className="card-lux p-4">
          <h3 className="font-display text-lg capitalize mb-2">{d}</h3>
          {["daily", "housekeeping"].map((kind) => (
            <div key={kind} className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">{kind === "daily" ? "Daily Auto Tasks" : "Housekeeping (idle prevention)"}</div>
              <div className="space-y-1">
                {templates.filter((t) => t.department === d && t.kind === kind).map((t) => (
                  <div key={t.id} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-sm bg-[#050505] border border-[#1a1a1a]" data-testid={`tpl-row-${t.id}`}>
                    <span className={`flex-1 ${t.active ? "text-zinc-300" : "text-zinc-600 line-through"}`}>{t.title}{t.requires_photo ? " 📷" : ""}</span>
                    <Switch checked={!!t.active} onCheckedChange={() => toggleTpl(t)} className="data-[state=checked]:bg-emerald-500 scale-75" data-testid={`tpl-toggle-${t.id}`} />
                    <button onClick={() => removeTpl(t)} className="text-zinc-600 hover:text-red-400" data-testid={`tpl-delete-${t.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
                {adding?.department === d && adding?.kind === kind ? (
                  <div className="flex gap-1.5">
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title" autoFocus className="bg-[#050505] border-[#2a2a2a] h-8 text-xs" data-testid="tpl-title-input" />
                    <Button onClick={addTpl} disabled={!title.trim()} className="h-8 text-xs bg-[#D4AF37] text-black" data-testid="tpl-save-btn">Add</Button>
                  </div>
                ) : (
                  <button onClick={() => { setAdding({ department: d, kind }); setTitle(""); }} data-testid={`tpl-add-${d}-${kind}`}
                    className="text-[11px] text-zinc-500 hover:text-[#D4AF37] flex items-center gap-1"><Plus className="w-3 h-3" /> Add</button>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
      </div>
    </div>
  );
}

function TeamTab({ employees, onChanged }) {
  const saveProfile = async (e, field, value) => {
    try {
      await api.patch(`/users/${e.id}/work-profile`, { [field]: value });
      toast.success(`${e.name} updated`);
      onChanged();
    } catch (err) { toast.error("Update failed"); }
  };
  return (
    <div className="card-lux p-5 overflow-x-auto" data-testid="tm-team">
      <h3 className="font-display text-lg mb-1">Team Work Profiles</h3>
      <p className="text-xs text-zinc-500 mb-3">Set each staff member's department and shift — used for daily auto-tasks and idle prevention.</p>
      <table className="w-full text-xs">
        <thead><tr className="text-zinc-500 text-left"><th className="py-2">Employee</th><th>Department</th><th>Shift Start</th><th>Shift End</th></tr></thead>
        <tbody>
          {employees.map((e) => (
            <tr key={e.id} className="border-t border-[#1a1a1a]" data-testid={`team-row-${e.id}`}>
              <td className="py-2 text-zinc-200">{e.name}</td>
              <td>
                <select defaultValue={e.department || ""} onChange={(ev) => saveProfile(e, "department", ev.target.value)} data-testid={`team-dept-${e.id}`}
                  className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 h-8 text-xs">
                  <option value="" disabled>—</option>
                  {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </td>
              <td><input type="time" defaultValue={e.shift_start || "09:00"} onBlur={(ev) => saveProfile(e, "shift_start", ev.target.value)} data-testid={`team-start-${e.id}`} className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 h-8 text-xs" /></td>
              <td><input type="time" defaultValue={e.shift_end || "18:00"} onBlur={(ev) => saveProfile(e, "shift_end", ev.target.value)} data-testid={`team-end-${e.id}`} className="bg-[#050505] border border-[#2a2a2a] rounded-sm px-2 h-8 text-xs" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
